import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, inr, fmtD, ago } from "@/lib/api";
import { PageHeader, Sel, VerticalBadge, StageBadge, Empty } from "@/components/common";
import { LeadFormDialog } from "@/components/LeadForm";

export default function Leads() {
  const { meta, isAdmin, salesUsers } = useAuth();
  const [f, setF] = useState({ q: "", vertical: "", source: "", stage: "", assigned_to: "", tag: "", status: "" });
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/leads", { params: p }).then((r) => setLeads(r.data));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHeader title="Leads" subtitle={`${leads.length} ${isAdmin ? "leads across the team" : "leads assigned to you"}`}>
        <button data-testid="create-lead-button" onClick={() => setOpen(true)} className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:opacity-90">
          <Plus className="h-4 w-4" /> Create lead
        </button>
      </PageHeader>
      <div className="crm-card mb-4 flex flex-wrap gap-3 p-3" data-testid="lead-filters">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="lead-search-input" className="crm-input pl-9" placeholder="Search company, contact, phone, email…" value={f.q} onChange={(e) => set("q")(e.target.value)} />
        </div>
        {isAdmin && <div className="w-44"><Sel testid="lead-filter-salesperson" value={f.assigned_to} onChange={set("assigned_to")} placeholder="All salespeople" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></div>}
        <div className="w-36"><Sel testid="lead-filter-vertical" value={f.vertical} onChange={set("vertical")} placeholder="All verticals" options={meta.verticals} /></div>
        <div className="w-40"><Sel testid="lead-filter-source" value={f.source} onChange={set("source")} placeholder="All sources" options={meta.sources} /></div>
        <div className="w-44"><Sel testid="lead-filter-stage" value={f.stage} onChange={set("stage")} placeholder="All stages" options={meta.stages} /></div>
        <div className="w-32"><Sel testid="lead-filter-tag" value={f.tag} onChange={set("tag")} placeholder="All tags" options={meta.tags} /></div>
      </div>
      <div className="crm-card overflow-x-auto">
        <table className="crm-table" data-testid="leads-table">
          <thead><tr>{["Company", "Contact", "Vertical", "Source", "Stage", "Deal value", "Prob.", "Owner", "Last contacted", "Next follow-up", "Created"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="cursor-pointer" onClick={() => nav(`/leads/${l.id}`)}>
                <td><Link to={`/leads/${l.id}`} className="font-semibold text-slate-900 hover:text-primary">{l.company}</Link>
                  <div className="mt-0.5 flex gap-1">{(l.tags || []).map((t) => <span key={t} className="rounded bg-slate-100 px-1.5 text-[10px] text-slate-600">{t}</span>)}</div></td>
                <td><div>{l.contact_person}</div><div className="text-xs text-muted-foreground">{l.phone}</div></td>
                <td><VerticalBadge v={l.vertical} /></td>
                <td className="text-xs">{l.source}</td>
                <td><StageBadge s={l.stage} /></td>
                <td className="num">{inr(l.deal_value)}</td>
                <td className="num text-xs">{l.probability}%</td>
                <td className="text-xs">{l.assigned_name}</td>
                <td className="text-xs text-muted-foreground">{ago(l.last_contacted)}</td>
                <td className={`text-xs ${l.next_follow_up && new Date(l.next_follow_up) < new Date() ? "font-semibold text-rose-600" : ""}`}>{fmtD(l.next_follow_up)}</td>
                <td className="text-xs text-muted-foreground">{fmtD(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!leads.length && <Empty text="No leads match your filters" />}
      </div>
      <LeadFormDialog open={open} onOpenChange={setOpen} onSaved={(l) => nav(`/leads/${l.id}`)} />
    </div>
  );
}
